import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Palette, Type, Mail, Image, FileText, RotateCcw, Check, AlertTriangle, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_OPTIONS = [
  { id: 'task_assignment', label: 'Task Assignment' },
  { id: 'meeting_processed', label: 'Meeting Processed' },
  { id: 'scheduled_report', label: 'Scheduled Report' },
];

const PRESET_COLORS = [
  '#2563eb', '#7c3aed', '#0891b2', '#059669', '#dc2626',
  '#d97706', '#4f46e5', '#0d9488', '#be185d', '#1d4ed8',
];

export const EmailBranding = () => {
  const { token } = useAuth();
  const [branding, setBranding] = useState(null);
  const [form, setForm] = useState({ displayName: '', replyToEmail: '', logoUrl: '', accentColor: '', footerText: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState('task_assignment');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const jsonH = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const safeFetch = async (url, opts = {}) => {
    const res = await fetch(url, opts);
    let data;
    try { data = await res.clone().json(); } catch { const t = await res.text(); try { data = JSON.parse(t); } catch { data = { detail: t }; } }
    return { ok: res.ok, data };
  };

  const fetchBranding = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await safeFetch(`${API_URL}/api/email-branding`, { headers: headers() });
    if (ok) {
      setBranding(data);
      setForm({
        displayName: data.displayName || '',
        replyToEmail: data.replyToEmail || '',
        logoUrl: data.logoUrl || '',
        accentColor: data.accentColor || '#2563eb',
        footerText: data.footerText || '',
      });
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    const { ok, data } = await safeFetch(
      `${API_URL}/api/email-branding/preview?template=${previewTemplate}`,
      { method: 'POST', headers: headers() }
    );
    if (ok) setPreviewHtml(data.html || '');
    setPreviewLoading(false);
  }, [headers, previewTemplate]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    const payload = {};
    if (form.displayName !== (branding?.displayName || '')) payload.displayName = form.displayName;
    if (form.replyToEmail !== (branding?.replyToEmail || '')) payload.replyToEmail = form.replyToEmail;
    if (form.logoUrl !== (branding?.logoUrl || '')) payload.logoUrl = form.logoUrl;
    if (form.accentColor !== (branding?.accentColor || '#2563eb')) payload.accentColor = form.accentColor;
    if (form.footerText !== (branding?.footerText || '')) payload.footerText = form.footerText;

    if (Object.keys(payload).length === 0) { setSaving(false); setSuccess('No changes to save'); return; }

    const { ok, data } = await safeFetch(`${API_URL}/api/email-branding`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(payload) });
    if (ok) {
      setBranding(data);
      setSuccess('Branding saved successfully');
      setDirty(false);
      // Refresh preview to show changes
      setTimeout(() => fetchPreview(), 500);
    } else {
      setError(data?.detail || 'Failed to save');
    }
    setSaving(false);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all branding to D3 Command defaults?')) return;
    setSaving(true); setError('');
    const { ok, data } = await safeFetch(`${API_URL}/api/email-branding/reset`, { method: 'POST', headers: headers() });
    if (ok) {
      setBranding(data);
      setForm({ displayName: data.displayName || '', replyToEmail: data.replyToEmail || '', logoUrl: data.logoUrl || '', accentColor: data.accentColor || '#2563eb', footerText: data.footerText || '' });
      setSuccess('Branding reset to defaults');
      setDirty(false);
      setTimeout(() => fetchPreview(), 500);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-zinc-400 text-sm">Loading branding settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-branding-tab">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-5">
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-5">
            <h3 className="text-white font-semibold flex items-center gap-2"><Palette className="w-4 h-4 text-blue-400" />Email Branding</h3>

            {/* Display Name */}
            <div>
              <label className="flex items-center gap-1.5 text-sm text-gray-400 mb-1.5"><Type className="w-3.5 h-3.5" />Display Name</label>
              <input type="text" value={form.displayName} onChange={(e) => handleChange('displayName', e.target.value)} maxLength={100} placeholder="D3 Command"
                className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 text-sm" data-testid="branding-display-name" />
              <p className="text-xs text-gray-600 mt-1">Shown as the sender name in email clients</p>
            </div>

            {/* Reply-To Email */}
            <div>
              <label className="flex items-center gap-1.5 text-sm text-gray-400 mb-1.5"><Mail className="w-3.5 h-3.5" />Reply-To Email</label>
              <input type="email" value={form.replyToEmail} onChange={(e) => handleChange('replyToEmail', e.target.value)} placeholder="support@yourcompany.com"
                className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 text-sm" data-testid="branding-reply-to" />
              <p className="text-xs text-gray-600 mt-1">Where replies are directed (optional)</p>
            </div>

            {/* Logo URL */}
            <div>
              <label className="flex items-center gap-1.5 text-sm text-gray-400 mb-1.5"><Image className="w-3.5 h-3.5" />Logo URL</label>
              <input type="url" value={form.logoUrl} onChange={(e) => handleChange('logoUrl', e.target.value)} placeholder="https://yourcompany.com/logo.png"
                className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 text-sm font-mono" data-testid="branding-logo-url" />
              <p className="text-xs text-gray-600 mt-1">Direct link to your logo (PNG/SVG, max 160px wide). Leave blank for default D3 logo.</p>
              {form.logoUrl && (
                <div className="mt-2 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                  <img src={form.logoUrl} alt="Logo preview" className="max-h-10 max-w-[160px]" onError={(e) => { e.target.style.display = 'none'; }} data-testid="branding-logo-preview" />
                </div>
              )}
            </div>

            {/* Accent Color */}
            <div>
              <label className="flex items-center gap-1.5 text-sm text-gray-400 mb-1.5"><Palette className="w-3.5 h-3.5" />Accent Color</label>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input type="color" value={form.accentColor} onChange={(e) => handleChange('accentColor', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-white/10 bg-transparent" data-testid="branding-color-picker" />
                </div>
                <input type="text" value={form.accentColor} onChange={(e) => handleChange('accentColor', e.target.value)} placeholder="#2563eb" maxLength={7}
                  className="w-28 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-blue-500/50" data-testid="branding-color-hex" />
                <div className="flex items-center gap-1">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => handleChange('accentColor', c)} title={c}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${form.accentColor === c ? 'border-white scale-110' : 'border-transparent hover:scale-110'}`}
                      style={{ backgroundColor: c }} data-testid={`color-preset-${c.slice(1)}`} />
                  ))}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-6 px-3 rounded flex items-center text-xs font-semibold text-white" style={{ backgroundColor: form.accentColor }}>
                  Button Preview
                </div>
                <span className="text-xs text-gray-600">Buttons and links use this color</span>
              </div>
            </div>

            {/* Footer Text */}
            <div>
              <label className="flex items-center gap-1.5 text-sm text-gray-400 mb-1.5"><FileText className="w-3.5 h-3.5" />Footer Text</label>
              <textarea value={form.footerText} onChange={(e) => handleChange('footerText', e.target.value)} rows={3} maxLength={500} placeholder="Company name and address..."
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 text-sm resize-none" data-testid="branding-footer-text" />
              <p className="text-xs text-gray-600 mt-1">{form.footerText.length}/500 characters</p>
            </div>
          </div>

          {/* Actions */}
          {success && <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2" data-testid="branding-success"><Check className="w-4 h-4" />{success}</div>}
          {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2" data-testid="branding-error"><AlertTriangle className="w-4 h-4" />{error}</div>}

          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving || !dirty}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              data-testid="save-branding-btn">
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
            <button onClick={handleReset} disabled={saving}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-sm"
              data-testid="reset-branding-btn">
              <RotateCcw className="w-4 h-4" />Reset
            </button>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-indigo-400" />Email Preview</h3>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5">
              {TEMPLATE_OPTIONS.map(t => (
                <button key={t.id} onClick={() => setPreviewTemplate(t.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${previewTemplate === t.id ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                  data-testid={`preview-template-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="email-preview-container">
            {previewLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-zinc-400 text-sm">Loading preview...</span>
              </div>
            ) : previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full border-0 bg-white rounded-2xl"
                style={{ height: '620px' }}
                sandbox="allow-same-origin"
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center py-20 text-zinc-500 text-sm">No preview available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
